import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { FileText, ExternalLink, CheckCircle, XCircle, Clock, CreditCard, Send, Trash2, Copy, Link2, Wallet } from "lucide-react";
import { API } from "../../App";

const statusConfig = {
  draft: { cls: "bg-zinc-100 text-zinc-600", label: "Draft", icon: Clock },
  sent: { cls: "bg-blue-100 text-blue-700", label: "Sent", icon: Send },
  approved: { cls: "bg-green-100 text-green-700", label: "Approved", icon: CheckCircle },
  rejected: { cls: "bg-red-100 text-red-700", label: "Rejected", icon: XCircle },
  paid: { cls: "bg-[#00341d] text-white", label: "Paid", icon: CreditCard },
};

const ViewChangeOrdersDialog = ({ dialog, setDialog, onDelete, baseUrl }) => {
  const [deleting, setDeleting] = useState(null);
  const [shortening, setShortening] = useState(null);
  const [billingBusy, setBillingBusy] = useState(null);

  const handleBillingMode = async (co, mode) => {
    setBillingBusy(co.id);
    try {
      await axios.post(`${API}/change-orders/${co.id}/billing-mode`, { mode });
      setDialog((p) => ({
        ...p,
        changeOrders: (p.changeOrders || []).map((c) => (c.id === co.id ? { ...c, billing_mode: mode } : c)),
      }));
      toast.success(mode === "quote_balance" ? "Added to quote balance" : "Will be billed separately");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update billing");
    } finally {
      setBillingBusy(null);
    }
  };

  const handleDelete = async (coId) => {
    setDeleting(coId);
    await onDelete(coId);
    setDeleting(null);
  };

  const handleCopyLink = async (co) => {
    let url = co.short_url;
    if (!url) {
      setShortening(co.id);
      try {
        const resp = await axios.post(`${API}/change-orders/${co.id}/shorten`);
        url = resp.data.short_url;
      } catch {
        const coBaseUrl = window.location.hostname.includes("landclearco.com")
          ? "https://quotes.landclearco.com"
          : window.location.origin;
        url = `${coBaseUrl}/change-order/${co.id}`;
      } finally {
        setShortening(null);
      }
    }
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success("Short link copied!");
  };

  const changeOrders = dialog.changeOrders || [];

  return (
    <Dialog open={dialog.open} onOpenChange={(open) => setDialog((p) => ({ ...p, open }))}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#00341d]" />
            Change Orders
          </DialogTitle>
          <DialogDescription>
            {dialog.quote?.quote_number} &mdash; {dialog.quote?.customer_name}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-3">
          {changeOrders.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-10 h-10 text-zinc-200 mx-auto mb-3" />
              <p className="text-sm text-zinc-400">No change orders yet</p>
            </div>
          ) : (
            changeOrders.map((co) => {
              const cfg = statusConfig[co.status] || statusConfig.draft;
              const Icon = cfg.icon;
              const isPaid = co.payment_status === "paid";
              const totalWithFee = Math.floor((co.subtotal || 0) * 1.03 * 100) / 100;
              const canDelete = co.status === "draft";
              const inBalance = co.billing_mode === "quote_balance";

              return (
                <div
                  key={co.id}
                  className="border border-zinc-200 rounded-lg p-4 hover:border-zinc-300 transition-colors"
                  data-testid={`change-order-item-${co.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-[#00341d]">{co.change_order_number}</span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
                          <Icon className="w-3 h-3" /> {cfg.label}
                        </span>
                        {isPaid && co.status !== "paid" && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#00341d] text-white">
                            <CreditCard className="w-3 h-3" /> Paid
                          </span>
                        )}
                        {!isPaid && inBalance && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700" data-testid={`co-in-balance-${co.id}`}>
                            <Wallet className="w-3 h-3" /> In quote balance
                          </span>
                        )}
                      </div>

                      {/* Line items summary */}
                      {co.line_items?.length > 0 && (
                        <div className="mt-2 text-sm text-zinc-600">
                          {co.line_items.map((item, idx) => (
                            <span key={item.id || idx}>
                              {item.service_name}
                              {idx < co.line_items.length - 1 ? ", " : ""}
                            </span>
                          ))}
                        </div>
                      )}

                      {co.notes && (
                        <p className="mt-1 text-xs text-zinc-400 truncate">{co.notes}</p>
                      )}

                      {co.invoice_number && (
                        <p className="mt-1 text-xs text-violet-600 font-medium">Invoice: {co.invoice_number}</p>
                      )}

                      {co.total_map_acres > 0 && (
                        <p className="mt-1 text-xs text-orange-600 font-medium">{co.total_map_acres.toFixed(2)} acres mapped</p>
                      )}

                      <div className="mt-2 flex items-center gap-4 text-xs text-zinc-400">
                        {co.created_at && (
                          <span>Created {new Date(co.created_at).toLocaleDateString()}</span>
                        )}
                        {co.sent_at && (
                          <span>Sent {new Date(co.sent_at).toLocaleDateString()}</span>
                        )}
                        {co.approved_at && (
                          <span>Approved {new Date(co.approved_at).toLocaleDateString()}</span>
                        )}
                        {co.paid_at && (
                          <span>Paid {new Date(co.paid_at).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>

                    <div className="text-right shrink-0">
                      <p className="font-bold text-[#00341d] text-lg">
                        ${co.subtotal?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </p>
                      {co.subtotal > 0 && (
                        <p className="text-xs text-zinc-400">
                          ${totalWithFee.toLocaleString(undefined, { minimumFractionDigits: 2 })} w/ CC fee
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => window.open(`/change-order/${co.id}`, "_blank")}
                      data-testid={`view-co-${co.id}`}
                    >
                      <ExternalLink className="w-3 h-3 mr-1" /> View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => handleCopyLink(co)}
                      disabled={shortening === co.id}
                      data-testid={`copy-co-link-${co.id}`}
                    >
                      {shortening === co.id
                        ? <><span className="w-3 h-3 mr-1 animate-spin rounded-full border border-zinc-400 border-t-transparent inline-block" />Getting...</>
                        : <><Link2 className="w-3 h-3 mr-1" />Copy Link</>
                      }
                    </Button>
                    {!isPaid && (
                      <Button
                        variant="outline"
                        size="sm"
                        className={`h-7 text-xs ${inBalance ? "text-amber-700 border-amber-300 hover:bg-amber-50" : ""}`}
                        onClick={() => handleBillingMode(co, inBalance ? "separate" : "quote_balance")}
                        disabled={billingBusy === co.id}
                        data-testid={`co-billing-toggle-${co.id}`}
                      >
                        <Wallet className="w-3 h-3 mr-1" />
                        {billingBusy === co.id ? "Saving..." : inBalance ? "Bill separately" : "Add to quote balance"}
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleDelete(co.id)}
                        disabled={deleting === co.id}
                        data-testid={`delete-co-${co.id}`}
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        {deleting === co.id ? "Deleting..." : "Delete"}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="flex justify-end pt-4 border-t border-zinc-100">
          <Button variant="outline" onClick={() => setDialog((p) => ({ ...p, open: false }))}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewChangeOrdersDialog;
